import { decodeJWT } from 'app/components/utils/utils';
import { AUTH_TOKEN } from 'app/constants';
import { HttpError, HttpErrorProps } from 'app/components/utils/http-error';
import { PaginationParams, PaginationResponseType } from 'app/console-api/utils/pagination';

export enum PurposeType {
  Production = 'production',
  Development = 'development'
}

export enum IntegrationStatus {
  Completed = 'complete',
  Incomplete = 'incomplete'
}

export enum AppType {
  TelegramBot = 'telegramBot',
  TelegramMiniApp = 'telegramMiniApp'
}

export enum ContactType {
  Email = 'email',
  Phone = 'phone',
  Link = 'link'
}

export enum ContactAccessLevel {
  Public = 'public',
  PartnersOnly = 'partners_only'
}

export interface Contact {
  type: ContactType;
  value: string;
  access: ContactAccessLevel;
}

export interface AppCreateRequest {
  name: string;
  description: string;
  type: AppType;
  categories: string[];
  contacts: Contact[];
  purpose: PurposeType;
  mau: number;
  dau: number;
}

export interface App {
  id: string;
  organizationID: string;
  name: string;
  description: string;
  url: string;
  type: string;
  appKey: string;
  claimNotificationUrl: string;
  apiSecretKey: string;
  categories: string[];
  contacts: Contact[];
  mau: number;
  dau: number;
  isActive: boolean;
  integrationStatus: IntegrationStatus;
  purpose: PurposeType;
  createdAt: Date;
  updatedAt: Date;
}

export interface AppViewResponse extends App {
  eventsStats: Record<string, number>;
}

export interface GetAppListFilter extends PaginationParams {
  isActive?: boolean;
}

export interface AppUpdateRequest {
  isActive: boolean;
  name: string;
  description: string;
  categories: string[];
  contacts: Contact[];
  mau: number;
  dau: number;
}

export interface AppIntegrationDataUpdateRequest {
  url: string;
  claimNotificationUrl: string;
}

export const getAppsList = async (data: GetAppListFilter): Promise<PaginationResponseType<App>> => {
  let token = localStorage.getItem(AUTH_TOKEN) || '';
  const searchParams = new URLSearchParams();
  if (data.isActive !== undefined) {
    searchParams.append('is_active', data.isActive.toString());
  }
  searchParams.append('pageSize', data.pageSize.toString());
  searchParams.append('currentPage', data.currentPage.toString());

  const response = await fetch(
    `${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/apps?${searchParams.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );
  if (!response.ok) {
    return Promise.reject(
      new HttpError(`Failed to get apps: ${response.statusText}`, response.status)
    );
  }
  return await response.json();
};

export const createApp = async (request: AppCreateRequest): Promise<App> => {
  const response = await fetch(`${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/apps`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN) || ''}`
    },
    body: JSON.stringify(request)
  });

  if (!response.ok) {
    if (response.body) {
      return response
        .clone()
        .json()
        .then((errorInfo: HttpErrorProps) => {
          return Promise.reject(
            new HttpError(
              `Failed to create app: ${response.statusText}`,
              response.status,
              errorInfo
            )
          );
        });
    } else {
      return Promise.reject(
        new HttpError(`Failed to create app: ${response.statusText}`, response.status)
      );
    }
  }

  return await response.json();
};

export const updateIntegrationData = async (
  appID: string,
  request: AppIntegrationDataUpdateRequest
): Promise<App> => {
  const response = await fetch(
    `${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/apps/${appID}/integration-data`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN) || ''}`
      },
      body: JSON.stringify(request)
    }
  );

  if (!response.ok) {
    if (response.body) {
      return response
        .clone()
        .json()
        .then((errorInfo: HttpErrorProps) => {
          return Promise.reject(
            new HttpError(
              `Failed to update integration data: ${response.statusText}`,
              response.status,
              errorInfo
            )
          );
        });
    } else {
      return Promise.reject(
        new HttpError(`Failed to update integration data: ${response.statusText}`, response.status)
      );
    }
  }
  return await response.json();
};

export const getAppById = async (id: string): Promise<AppViewResponse> => {
  const response = await fetch(
    `${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/apps/${id}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN) || ''}`
      }
    }
  );
  if (!response.ok) {
    return Promise.reject(
      new HttpError(`Failed to get apps: ${response.statusText}`, response.status)
    );
  }
  return await response.json();
};

export const updateApp = async (id: string, request: AppUpdateRequest): Promise<App> => {
  const response = await fetch(
    `${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/apps/${id}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN) || ''}`
      },
      body: JSON.stringify(request)
    }
  );

  if (!response.ok) {
    if (response.body) {
      return response
        .clone()
        .json()
        .then((errorInfo: HttpErrorProps) => {
          return Promise.reject(
            new HttpError(
              `Failed to update app: ${response.statusText}`,
              response.status,
              errorInfo
            )
          );
        });
    } else {
      return Promise.reject(
        new HttpError(`Failed to create app: ${response.statusText}`, response.status)
      );
    }
  }
  return await response.json();
};

export interface PublicAppViewResponse {
  id: string;
  organizationID: string;
  name: string;
  description: string;
  url: string;
  type: string;
  categories: string[];
  contacts: Contact[];
  isActive: boolean;
  integrationStatus: IntegrationStatus;
  mau: number;
  dau: number;
  purpose: PurposeType;
  createdAt: Date;
  updatedAt: Date;
}

export const getPublicAppView = async (appId: string): Promise<PublicAppViewResponse> => {
  const token = localStorage.getItem(AUTH_TOKEN);
  const headers: HeadersInit = {};

  // Include auth token if available (to check partnership status)
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(
    `${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/apps/${appId}/public-view`,
    {
      headers
    }
  );

  if (!response.ok) {
    return Promise.reject(
      new HttpError(`Failed to get public app view: ${response.statusText}`, response.status)
    );
  }

  return await response.json();
};
